import { Location } from '@angular/common';
import { AfterViewChecked, Component, OnInit, TemplateRef, ViewChild, computed, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { ScheduledTask, SchedulerBulkActionTaskModalInfo, SchedulerBulkActionType } from '../../shared/models/scheduled-task.model';
import { Role } from '../../shared/models/user.model';
import { DialogRef, ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { SchedulerSearchEvent, SchedulerService, SchedulerTaskActionData, SchedulerTaskView } from '../../shared/services/scheduler.service';
import { UsersService } from '../../shared/services/users.service';
import { SchedulerSearchComponent } from '../scheduler-search/scheduler-search.component';
import { SchedulerTableComponent } from '../scheduler-table/scheduler-table.component';

@Component({
  standalone: false,
  selector: 'app-scheduler-index',
  templateUrl: './scheduler-index.component.html',
  styleUrls: ['./scheduler-index.component.scss'],
})
export class SchedulerIndexComponent implements OnInit, AfterViewChecked {
  @ViewChild(SchedulerTableComponent, { static: false }) table: SchedulerTableComponent;
  @ViewChild(SchedulerSearchComponent, { static: false }) schedulerSearchComponent: SchedulerSearchComponent;
  @ViewChild('schedulerCreateModalTpl', { read: TemplateRef, static: false }) schedulerCreateModalTpl: TemplateRef<HTMLElement>;
  @ViewChild('schedulerStopTaskModalTpl', { read: TemplateRef, static: false }) schedulerStopTaskModalTpl: TemplateRef<HTMLElement>;
  @ViewChild('schedulerBulkActionTaskModalTpl', { read: TemplateRef, static: false }) schedulerBulkActionTaskModalTpl: TemplateRef<HTMLElement>;
  schedulerCreateModal: DialogRef;
  schedulerStopTaskModal: DialogRef;
  schedulerBulkActionTaskModal: DialogRef;
  hiddenMode: boolean;
  showMyTasks: boolean;
  isTableInitialized = false;
  isAdmin: boolean;
  serviceDisabled = null;
  selectedStoppableCount = 0;
  batchCompleted = 0;
  searchControl = new FormControl();
  readonly activeTasksView = signal<SchedulerTaskView>('active');

  readonly selectedTasks = signal<ScheduledTask[]>([]);
  readonly schedulerBulkActionTaskModalInfo = signal<SchedulerBulkActionTaskModalInfo>({
    actionType: 'stop',
    taskIds: [],
  });

  readonly selectedStoppableTasks = computed(() => {
    const tasks = this.selectedTasks() || [];

    return tasks.filter(task => SchedulerService.canStopTask(task));
  });

  readonly selectedPausableTasks = computed(() => {
    const tasks = this.selectedTasks() || [];

    return tasks.filter(task => SchedulerService.canPauseTask(task));
  });

  readonly selectedResumableTasks = computed(() => {
    const tasks = this.selectedTasks() || [];

    return tasks.filter(task => SchedulerService.canResumeTask(task));
  });

  readonly bulkActionTaskIdsString = computed(() => this.schedulerBulkActionTaskModalInfo().taskIds.join(' '));
  readonly bulkActionModalTitle = computed(() => {
    const actionType = this.schedulerBulkActionTaskModalInfo().actionType;

    switch (actionType) {
      case 'pause':
        return 'Pause selected tasks?';
      case 'resume':
        return 'Resume selected tasks?';
      default:
        return 'Stop selected tasks?';
    }
  });
  readonly bulkActionModalDescription = computed(() => {
    const actionType = this.schedulerBulkActionTaskModalInfo().actionType;

    switch (actionType) {
      case 'pause':
        return 'The following task IDs will be paused:';
      case 'resume':
        return 'The following task IDs will be resumed:';
      default:
        return 'The following task IDs will be stopped:';
    }
  });
  readonly bulkActionModalConfirmText = computed(() => {
    const actionType = this.schedulerBulkActionTaskModalInfo().actionType;

    switch (actionType) {
      case 'pause':
        return 'Pause';
      case 'resume':
        return 'Play';
      default:
        return 'Stop';
    }
  });

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    public notificationService: NotificationService,
    public service: SchedulerService,
    public usersService: UsersService,
    titleService: Title,
    private modal: ModalService,
    private location: Location
  ) {
    titleService.setTitle('Scheduled tasks');
    this.router = router;
    this.notificationService = notificationService;
    this.hiddenMode = this.service.getHiddenMode(this.activeTasksView());
    this.showMyTasks = this.service.getShowMyTasksFlag(this.activeTasksView());
  }

  ngOnInit() {
    this.usersService.can('admin').then(admin => {
      this.isAdmin = admin;
    });

    const taskView = this.parseTaskView(this.route.snapshot.queryParamMap.get('view'));
    this.applyTaskViewState(taskView);
  }

  ngAfterViewChecked() {
    if (!this.isTableInitialized) {
      this.route.queryParams.subscribe(queryParams => {
        if (queryParams.ids && this.table) {
          this.isTableInitialized = true;
          const ids = queryParams.ids.split(',');
          this.schedulerSearchComponent.filters.forEach(_ => {
            if (_.filterKey === 'ids') _.show = true;
          });
          this.schedulerSearchComponent.form.patchValue({ ids: queryParams.ids });

          this.table.currentPageNumber = 1;
          this.table.requestParams.searchData = this.service.normalizeSearchDataForTaskView(
            {
              ids,
            },
            this.activeTasksView(),
            this.hiddenMode,
            this.showMyTasks
          );
        }
      });
    }
  }

  onScheduleAfterSave(): void {
    this.schedulerCreateModal.close();
    this.clearUrlQuery();
    this.table.update();
  }

  clearUrlQuery(): void {
    // Clear the URL with any ids present ...
    const currentPath = this.location.path();
    const currentHistoryState = this.location.getState();
    const newPath = currentPath.split('?')[0];
    this.location.replaceState(newPath, '', currentHistoryState);
  }

  schedulerAction(event: SchedulerTaskActionData) {
    if (event.name === 'clone') {
      if (event.row.state === 'SCHEDULED' || event.row.state === 'RUNNING') {
        this.schedulerStopTaskModal = this.modal.alert().dialogClass('modal-dialog small-modal').component(this.schedulerStopTaskModalTpl).open();
        this.schedulerStopTaskModal.result.then((res: boolean) => {
          if (res === null) {
            this.schedulerStopTaskModal.close();
            return;
          }
          if (res) {
            this.stopTask(event);
          } else {
            this.notificationService.info('Current task has not been stopped.');
            this.openModal(event);
          }
        });
      } else {
        this.openModal(event);
      }
    }
  }

  stopTask(event: SchedulerTaskActionData) {
    this.service.stop(event.row.id).subscribe({
      next: () => {
        this.notificationService.success('Current task has been stopped');
        this.openModal(event);
      },
      error: error => {
        console.error(error);
        this.notificationService.error('Cannot stop current task. Please try again later.', 'Scheduler');
      },
    });
  }

  openModal(event: SchedulerTaskActionData) {
    const currentUrl = this.router.url.split('?')[0];
    const navigationExtras: NavigationExtras = {
      queryParams: { id: event.row.id },
      queryParamsHandling: 'merge',
      preserveFragment: true,
    };
    this.router.navigate([currentUrl], navigationExtras);
    this.schedulerCreateModal = this.modal.alert().dialogClass('modal-dialog full-modal').component(this.schedulerCreateModalTpl).open();
    this.schedulerCreateModal.result.then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  onChangeHiddenMode() {
    this.service.saveHiddenMode(this.activeTasksView(), this.hiddenMode);
    this.table.currentPageNumber = 1;
    this.table.requestParams.searchData = this.getNormalizedSearchData(this.table.requestParams.searchData);
    this.table.update();
  }

  onShowMyTasksFlag() {
    this.service.saveShowMyTasksFlag(this.activeTasksView(), this.showMyTasks);
    this.table.currentPageNumber = 1;
    this.table.requestParams.searchData = this.getNormalizedSearchData(this.table.requestParams.searchData);
    this.table.update();
  }

  onChangeInputSearch(phrase) {
    this.table.requestParams.search = phrase;
    this.table.requestParams.page = 1;
    this.table.update();
  }

  onClickCreateSchedule(): void {
    this.schedulerCreateModal = this.modal.alert().dialogClass('modal-dialog full-modal').component(this.schedulerCreateModalTpl).open();
    this.schedulerCreateModal.result.then(() => {
      this.clearUrlQuery();
    });
  }

  onSubmitSearchForm(event: SchedulerSearchEvent) {
    if (this.table.loading) {
      return;
    }
    this.clearUrlQuery();
    this.table.currentPageNumber = 1;
    this.table.requestParams.searchData = this.getNormalizedSearchData(event);
    this.table.update();
  }

  onTasksViewChange(taskView: SchedulerTaskView): void {
    if (this.activeTasksView() === taskView) {
      return;
    }

    this.clearTaskSelection();
    this.applyTaskViewState(taskView);
    this.schedulerSearchComponent?.resetAll();
    this.clearUrlQuery();

    if (this.table) {
      this.table.currentPageNumber = 1;
      this.table.requestParams.searchData = this.getNormalizedSearchData(this.service.getEmptySearchObject());
      this.table.update();
    }
  }

  onSelectionChange(tasks: ScheduledTask[]): void {
    this.selectedTasks.set(tasks ?? []);
  }

  onClickBulkStop(): void {
    this.openBulkActionModal('stop', this.selectedStoppableTasks());
  }

  onClickBulkPause(): void {
    this.openBulkActionModal('pause', this.selectedPausableTasks());
  }

  onClickBulkResume(): void {
    this.openBulkActionModal('resume', this.selectedResumableTasks());
  }

  openBulkActionModal(action: SchedulerBulkActionType, tasks: ScheduledTask[]): void {
    this.schedulerBulkActionTaskModalInfo.set({
      actionType: action,
      taskIds: tasks.map(task => task.id),
    });
    this.schedulerBulkActionTaskModal = this.modal
      .alert()
      .dialogClass('modal-dialog small-modal')
      .component(this.schedulerBulkActionTaskModalTpl)
      .open();

    this.schedulerBulkActionTaskModal.result.then((res: boolean) => {
      if (res) this.runBulkAction(tasks);
    });
  }

  batchClear(): void {
    this.clearTaskSelection();
  }

  runBulkAction(tasks: ScheduledTask[]): void {
    const action = this.schedulerBulkActionTaskModalInfo().actionType;
    const actionServiceMethod = action === 'pause' ? 'bulkPause' : action === 'resume' ? 'bulkResume' : 'bulkStop';
    const actionDoneVerb = action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'stopped';

    this.service[actionServiceMethod](tasks.map(task => task.id)).subscribe({
      next: () => {
        this.notificationService.success(`${tasks.length} task(s) have been ${actionDoneVerb}.`);
        this.selectedTasks.set([]);
        this.schedulerBulkActionTaskModalInfo.update(info => ({
          ...info,
          taskIds: [],
        }));
        this.table.clearSelection();
        this.table.update();
      },
      error: e => {
        console.error(e);
        this.notificationService.error({
          title: 'Scheduler',
          message: `Bulk ${action} failed`,
          requestMessage: e.statusText,
          requestCode: e.status,
        });
      },
    });
  }

  private getNormalizedSearchData(searchData: Partial<SchedulerSearchEvent> | null | undefined): SchedulerSearchEvent {
    return this.service.normalizeSearchDataForTaskView(searchData, this.activeTasksView(), this.hiddenMode, this.showMyTasks);
  }

  private clearTaskSelection(): void {
    this.selectedTasks.set([]);
    this.table?.clearSelection();
  }

  private parseTaskView(view: string | null | undefined): SchedulerTaskView {
    return view === 'inactive' ? 'inactive' : 'active';
  }

  private applyTaskViewState(taskView: SchedulerTaskView): void {
    this.activeTasksView.set(taskView);
    this.hiddenMode = this.service.getHiddenMode(taskView);
    this.showMyTasks = this.service.getShowMyTasksFlag(taskView);
  }

  protected readonly Role = Role;
}
